import React, { useState } from 'react';
import { X, Mail, Lock, ShieldAlert, Eye, EyeOff } from 'lucide-react';
import { getAuth, signInWithPopup, GoogleAuthProvider, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { Config } from '../../types';
import { FadeInImage } from './FadeInImage';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialView?: 'login' | 'register';
  config?: Config;
}

export function LoginModal({ isOpen, onClose, onSuccess, initialView = 'login', config }: LoginModalProps) {
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setIsPasswordReset(false);
      setError('');
      setSuccessMessage('');
    }
  }, [isOpen, initialView]);

  if (!isOpen) return null;

  const auth = getAuth();
  auth.languageCode = 'es';
  const primaryColor = config?.primaryColor || '#eab308';

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      await signInWithPopup(auth, provider);
      
      window.sessionStorage.setItem('justLoggedIn', 'true');
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/popup-blocked') {
        setError('El navegador bloqueó la ventana emergente.');
      } else if (err.code !== 'auth/popup-closed-by-user') {
        setError('Error al iniciar sesión con Google.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      // Intentamos iniciar sesión primero
      await signInWithEmailAndPassword(auth, email, password);
      window.sessionStorage.setItem('justLoggedIn', 'true');
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error(err);
      // Si el usuario no existe (auth/user-not-found) o credenciales invalidas que puedan indicar que no existe
      // En Firebase Auth V9, a veces lanza 'auth/invalid-credential' para todo, pero si estamos seguros que queremos auto-crear:
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-login-credentials' || err.code === 'auth/invalid-credential') {
        try {
          // Intentamos registrar al usuario con esa misma contraseña si falló el inicio de sesión
          // Nota: auth/invalid-credential también sale cuando la contraseña está mal de una cuenta que SÍ existe.
          // Para ser más seguros, primero intentamos signInMethodsForEmail, pero Firebase a veces lo tiene deshabilitado por protección.
          // Lo intentaremos registrar. Si ya existe, tirará email-already-in-use.
          await createUserWithEmailAndPassword(auth, email, password);
          localStorage.setItem('pwa_registration_timestamp', Date.now().toString());
          window.sessionStorage.setItem('justLoggedIn', 'true');
          onSuccess();
          onClose();
        } catch (registerErr: any) {
          if (registerErr.code === 'auth/email-already-in-use') {
            // Significa que la cuenta sí existía, y el error original era que la contraseña estaba mal
            setError('Credenciales incorrectas.');
          } else if (registerErr.code === 'auth/weak-password') {
            setError('La contraseña debe tener al menos 6 caracteres.');
          } else {
            setError('Ocurrió un error. Verifica tus datos e intenta de nuevo.');
          }
        }
      } else if (err.code === 'auth/wrong-password') {
        setError('Credenciales incorrectas.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Demasiados intentos. Por favor intenta más tarde.');
      } else {
        setError('Ocurrió un error. Verifica tus datos e intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Por favor, ingresa tu correo electrónico.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const functionsInstance = getFunctions();
      const sendCustomResetEmail = httpsCallable(functionsInstance, 'sendCustomPasswordResetEmail');
      const response: any = await sendCustomResetEmail({ email });
      
      if (response.data?.success) {
        setSuccessMessage('¡Enlace enviado! Revisa tu bandeja de entrada para restablecer tu contraseña.');
      } else {
        setError(response.data?.message || 'Error al enviar el enlace de recuperación.');
      }
    } catch (err: any) {
      console.error(err);
      setError('Error al enviar el enlace de recuperación. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-[#1e293b]/95 backdrop-blur-xl rounded-[2rem] md:rounded-[var(--radius-large)] border border-white/10 w-full max-w-md shadow-2xl relative overflow-hidden max-h-[95vh] overflow-y-auto custom-scrollbar">
        
        {/* Glow effect */}
        <div 
          className="absolute -top-32 -right-32 w-64 h-64 rounded-full blur-[80px] opacity-20 pointer-events-none"
          style={{ backgroundColor: primaryColor }}
        />

        <button onClick={onClose} className="absolute top-6 right-6 p-2 bg-black/40 hover:bg-white/10 rounded-full transition-all text-white/50 hover:text-white z-20 shadow-lg">
          <X size={20} />
        </button>

        <div className="p-6 md:p-8 relative z-10 flex flex-col items-center">
          {/* Logo / Branding */}
          {config?.logoUrl ? (
            <FadeInImage src={config.logoUrl} alt="App Logo" className="w-16 h-16 md:w-24 md:h-24 object-contain mb-4 drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]" noPlaceholderBg={true} />
          ) : (
            <div 
               className="w-16 h-16 rounded-full flex items-center justify-center mb-4 border"
               style={{ backgroundColor: `${primaryColor}15`, borderColor: `${primaryColor}30`, color: primaryColor }}
            >
               <span className="text-2xl font-black">{config?.pageName?.charAt(0) || 'L'}</span>
            </div>
          )}

          <h2 className="text-xl md:text-3xl font-black uppercase tracking-wider text-white text-center mb-1">
            {isPasswordReset ? 'Recuperar Clave' : '¡Bienvenido!'}
          </h2>
          <p className="opacity-60 text-[10px] md:text-sm font-bold text-center mb-6 md:mb-8 max-w-[280px]">
            {isPasswordReset 
              ? 'Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.' 
              : 'Ingresa para ver tus resultados y gestionar tus torneos.'}
          </p>

          {error && (
            <div className="w-full bg-red-500/10 text-red-400 text-xs font-bold text-center px-4 py-3 rounded-xl border border-red-500/20 mb-6 flex flex-col sm:flex-row items-center gap-2 justify-center">
              <ShieldAlert size={16} className="shrink-0" /> 
              <span>{error}</span>
            </div>
          )}

          {successMessage && (
            <div className="w-full bg-[#bef264]/10 text-[#bef264] text-xs font-bold text-center px-4 py-3 rounded-xl border border-[#bef264]/20 mb-6 flex flex-col sm:flex-row items-center gap-2 justify-center">
              <ShieldAlert size={16} className="shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {!isPasswordReset && (
            <>
              <div className="w-full relative mb-6">
                <button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="group w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-black font-black py-4 px-2 rounded-xl transition-all shadow-xl uppercase tracking-widest active:scale-[0.98] disabled:opacity-50 text-[11px] md:text-sm"
                >
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5 shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="truncate">Continuar con Google</span>
                </button>
              </div>

              <div className="flex items-center gap-4 mb-6 w-full opacity-60">
                <div className="h-px bg-white/20 flex-1"></div>
                <span className="text-[8px] md:text-[9px] font-black uppercase tracking-widest text-white/60 shrink-0">O usa tu email</span>
                <div className="h-px bg-white/20 flex-1"></div>
              </div>
            </>
          )}

          {isPasswordReset ? (
            <form onSubmit={handlePasswordReset} className="w-full space-y-4">
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 opacity-30 group-focus-within:opacity-100 transition-opacity" style={{ color: primaryColor }} size={18} />
                <input
                  type="email"
                  required
                  placeholder="tu@correo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 focus:border-white/50 outline-none font-bold text-sm text-white transition-colors"
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 text-black font-black py-4 rounded-xl transition-all uppercase tracking-widest text-sm shadow-[0_0_20px_rgba(0,0,0,0.5)] disabled:opacity-50 hover:opacity-90 active:scale-[0.98]"
                style={{ backgroundColor: primaryColor }}
              >
                {loading ? 'Enviando...' : 'Enviar enlace'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleEmailAction} className="w-full space-y-4">
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 opacity-30 group-focus-within:opacity-100 transition-opacity" style={{ color: primaryColor }} size={18} />
                <input
                  type="email"
                  required
                  placeholder="tu@correo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 focus:border-white/50 outline-none font-bold text-sm text-white transition-colors"
                />
              </div>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 opacity-30 group-focus-within:opacity-100 transition-opacity" style={{ color: primaryColor }} size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Tu contraseña secreta"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-12 py-3.5 focus:border-white/50 outline-none font-bold text-sm text-white transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 opacity-30 hover:opacity-100 transition-opacity text-white focus:outline-none"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="text-right">
                <button
                  type="button"
                  onClick={() => {
                    setIsPasswordReset(true);
                    setError('');
                    setSuccessMessage('');
                  }}
                  className="text-[9px] font-black uppercase tracking-widest text-white/40 hover:text-white transition-colors cursor-pointer select-none"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 text-black font-black py-4 rounded-xl transition-all uppercase tracking-widest text-sm shadow-[0_0_20px_rgba(0,0,0,0.5)] disabled:opacity-50 hover:opacity-90 active:scale-[0.98]"
                style={{ backgroundColor: primaryColor }}
              >
                {loading ? 'Cargando...' : 'Continuar con Correo'}
              </button>
            </form>
          )}

          <div className="mt-8 text-center w-full pt-4 border-t border-white/5 flex flex-col gap-3">
            {isPasswordReset && (
              <button 
                type="button" 
                onClick={() => { setIsPasswordReset(false); setError(''); setSuccessMessage(''); }}
                className="text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-white transition-colors cursor-pointer"
              >
                Volver a Iniciar Sesión
              </button>
            )}
            
            {!isPasswordReset && (
              <p className="text-[8px] opacity-30 mt-2 max-w-[200px] mx-auto leading-relaxed">
                Si no tienes cuenta, crearemos una automáticamente. Al continuar aceptas los Términos y la Política de Privacidad.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}